import type { FeatureCollection } from 'geojson';

import { apiClient } from './apiClient';
import { normaliseFeatureCollectionResponse } from './geoJsonUtils';

/**
 * Fetches the current positions of every active vehicle as a GeoJSON FeatureCollection.
 * Calls GET /vehicles/md — gated server-side by the per-user vehicle-tracking permission
 * (Origin user AND can_view_vehicle_tracking); a 403 means the caller lacks the capability.
 * Vehicles are Origin's own fleet, not client data, so no clientId is sent.
 * Returns a normalised GeoJSON FeatureCollection; never throws for invalid data.
 */
export async function fetchVehiclesMapData(): Promise<FeatureCollection> {
    const response = await apiClient.get<unknown>('/vehicles/md');
    return normaliseFeatureCollectionResponse(response.data);
}

/** Wire shape of GET /vehicles/{id} (camelCase JSON DTO, unlike the snake_case GeoJSON props). */
export interface VehicleDetails {
    id: string;
    displayName: string;
    registration?: string | null;
    vehicleType?: string | null;
    isActive: boolean;
    /** moving | stationary | parked | stale | offline | unknown — derived server-side. */
    state: string;
    latitude?: number | null;
    longitude?: number | null;
    /** ISO-8601 UTC instants. */
    positionTime?: string | null;
    lastUpdated?: string | null;
    speedKph?: number | null;
    heading?: number | null;
    ignitionOn?: boolean | null;
    isStandstill?: boolean | null;
    operativeName?: string | null;
}

/**
 * Fetches one vehicle's details. Calls GET /vehicles/{id} — same per-user gate as the map
 * data; 404 when the vehicle does not exist.
 */
export async function fetchVehicleDetails(vehicleId: string): Promise<VehicleDetails> {
    const response = await apiClient.get<VehicleDetails>(`/vehicles/${encodeURIComponent(vehicleId)}`);
    return response.data;
}

export type VehicleTripTrackStatus =
    | 'pending'
    | 'route_available'
    | 'summary_only'
    | 'failed_retryable'
    | 'unavailable';

export interface VehicleTrip {
    id: string;
    /** ISO-8601 UTC; display formatting is Europe/London. */
    startTime: string;
    endTime: string;
    startDescription?: string | null;
    endDescription?: string | null;
    startLatitude?: number | null;
    startLongitude?: number | null;
    endLatitude?: number | null;
    endLongitude?: number | null;
    distanceM?: number | null;
    durationS?: number | null;
    operativeName?: string | null;
    trackStatus: VehicleTripTrackStatus;
}

export interface VehicleTripsResponse {
    items: VehicleTrip[];
}

export interface VehicleTrackPoint {
    latitude: number;
    longitude: number;
    /** ISO-8601 UTC; absent for summary coordinates. */
    positionTime?: string | null;
}

export interface VehicleTripTrack {
    status: VehicleTripTrackStatus;
    /** GeoJSON-shaped LineString of genuine track points; only present when route_available. */
    route?: { type: string; coordinates: number[][] } | null;
    /** Genuine point(s) to show as markers for a locations-only trip; never drawn as a line. */
    isolatedPoints?: VehicleTrackPoint[] | null;
    genuinePointCount: number;
    startPoint?: VehicleTrackPoint | null;
    endPoint?: VehicleTrackPoint | null;
}

/**
 * Fetches a vehicle's completed trips for an inclusive Europe/London calendar-day range.
 * Calls GET /vehicles/{id}/trips?from&to (yyyy-MM-dd, range ≤ 31 days) — only trips from
 * the authoritative position source are returned.
 */
export async function fetchVehicleTrips(vehicleId: string, from: string, to: string): Promise<VehicleTripsResponse> {
    const response = await apiClient.get<VehicleTripsResponse>(`/vehicles/${encodeURIComponent(vehicleId)}/trips`, {
        params: { from, to },
    });
    return response.data;
}

/**
 * Fetches the travelled route (or its honest absence) for one completed trip. The route is
 * built exclusively from genuine provider track points — a trip without one shows markers,
 * never a fabricated line. 404 covers missing/foreign/non-authoritative trips alike.
 */
export async function fetchVehicleTripTrack(vehicleId: string, tripId: string): Promise<VehicleTripTrack> {
    const response = await apiClient.get<VehicleTripTrack>(
        `/vehicles/${encodeURIComponent(vehicleId)}/trips/${encodeURIComponent(tripId)}/track`,
    );
    return response.data;
}
